import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container, Row, Col, Card, Form, Button, Badge,
  ProgressBar, Alert, ListGroup, Modal, Tabs, Tab
} from 'react-bootstrap';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, Radar, RadarChart, PolarGrid,
  PolarAngleAxis, PolarRadiusAxis, Cell
} from 'recharts';

const GCCImpactAnalyzer = () => {
  const [industryData, setIndustryData] = useState([]);
  const [factors, setFactors] = useState([]);
  const [subFactors, setSubFactors] = useState([]);
  const [scores, setScores] = useState([]);
  const [selectedIndustry, setSelectedIndustry] = useState(0);
  const [selectedFactors, setSelectedFactors] = useState([]);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedSubFactor, setSelectedSubFactor] = useState(null);

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [
          industryRes,
          factorsRes,
          subFactorsRes,
          scoresRes
        ] = await Promise.all([
          axios.get('/api/industry-types'),
          axios.get('/api/factors'),
          axios.get('/api/subfactors'),
          axios.get('/api/scores')
        ]);

        setIndustryData(industryRes.data);
        setFactors(factorsRes.data);
        setSubFactors(subFactorsRes.data);
        setScores(scoresRes.data);
        
        // Pre-select all factors by default
        setSelectedFactors(factorsRes.data.map(f => f.id));
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Calculate impact scores based on selections
  useEffect(() => {
    if (scores.length === 0 || selectedFactors.length === 0) return;

    const calculateImpact = () => {
      // Filter scores by selected industry and factors
      const filteredScores = scores.filter(score => 
        score.industryId === selectedIndustry && 
        selectedFactors.includes(score.subFactorId.split('.')[0])
      );

      // Calculate average score
      const totalScore = filteredScores.reduce((sum, score) => sum + score.score, 0);
      const averageScore = filteredScores.length > 0 ? totalScore / filteredScores.length : 0;

      // Group by factor for detailed analysis
      const factorBreakdown = factors.map(factor => {
        const factorScores = filteredScores.filter(
          score => score.subFactorId.split('.')[0] === factor.id
        );
        const factorAvg = factorScores.length > 0 ? 
          factorScores.reduce((sum, score) => sum + score.score, 0) / factorScores.length : 0;
        
        return {
          factorId: factor.id,
          factorName: factor.name,
          score: factorAvg,
          subFactors: factorScores.map(score => ({
            subFactorId: score.subFactorId,
            score: score.score,
            ...subFactors.find(sf => sf.subFactorId === score.subFactorId)
          }))
        };
      });

      // Identify strengths and weaknesses
      const sortedFactors = [...factorBreakdown].sort((a, b) => b.score - a.score);
      const strengths = sortedFactors.slice(0, 3);
      const weaknesses = sortedFactors.slice(-3).reverse();

      setAnalysisResult({
        overallScore: averageScore,
        factorBreakdown,
        strengths,
        weaknesses,
        benchmarkComparison: calculateBenchmarkComparison(averageScore, selectedIndustry)
      });
    };

    calculateImpact();
  }, [scores, selectedIndustry, selectedFactors, factors, subFactors]);

  const calculateBenchmarkComparison = (score, industryId) => {
    // In a real application, this would compare against industry benchmarks
    // For now, we'll use simple mock data
    const industry = industryData.find(ind => ind.id === industryId);
    const industryAvg = 72; // Mock average score
    const topPerformerAvg = 88; // Mock top performer score
    
    return {
      industryAverage: industryAvg,
      topPerformer: topPerformerAvg,
      percentile: Math.round((score / topPerformerAvg) * 100),
      comparison: score > industryAvg ? 'above' : 'below'
    };
  };

  const handleFactorToggle = (factorId) => {
    if (selectedFactors.includes(factorId)) {
      setSelectedFactors(selectedFactors.filter(id => id !== factorId));
    } else {
      setSelectedFactors([...selectedFactors, factorId]);
    }
  };

  const handleSubFactorClick = (subFactor) => {
    setSelectedSubFactor(subFactor);
    setShowDetailModal(true);
  };

  const getScoreColor = (score) => {
    if (score >= 85) return '#27ae60'; // Green
    if (score >= 70) return '#f39c12'; // Orange
    return '#e74c3c'; // Red
  };

  const getScoreLabel = (score) => {
    if (score >= 85) return 'Excellent';
    if (score >= 70) return 'Good';
    if (score >= 60) return 'Fair';
    return 'Needs Improvement';
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading GCC Impact Analyzer...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <Row>
        <Col lg={3} className="mb-4">
          <Card className="h-100 shadow-sm">
            <Card.Header className="bg-primary text-white">
              <h5 className="mb-0">GCC Impact Analysis</h5>
            </Card.Header>
            <Card.Body>
              <Form.Group className="mb-4">
                <Form.Label>Select Industry</Form.Label>
                <Form.Select 
                  value={selectedIndustry} 
                  onChange={(e) => setSelectedIndustry(parseInt(e.target.value))}
                >
                  {industryData.map(industry => (
                    <option key={industry.id} value={industry.id}>
                      {industry.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group>
                <Form.Label>Select Factors to Include</Form.Label>
                <div className="factors-list">
                  {factors.map(factor => (
                    <Form.Check
                      key={factor.id}
                      type="checkbox"
                      id={`factor-${factor.id}`}
                      label={factor.name}
                      checked={selectedFactors.includes(factor.id)}
                      onChange={() => handleFactorToggle(factor.id)}
                      className="mb-2"
                    />
                  ))}
                </div>
              </Form.Group>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={9}>
          {analysisResult ? (
            <>
              <Row className="mb-4">
                <Col md={4}>
                  <Card className="text-center h-100 shadow-sm">
                    <Card.Body>
                      <h6 className="text-muted">Overall Impact Score</h6>
                      <h1
                        style={{ color: getScoreColor(analysisResult.overallScore) }}
                        className="display-4 fw-bold"
                      >
                        {analysisResult.overallScore.toFixed(1)}
                      </h1>
                      <Badge bg={analysisResult.overallScore >= 70 ? "success" : "warning"}>
                        {getScoreLabel(analysisResult.overallScore)}
                      </Badge>
                      <div className="mt-3">
                        <small className="text-muted">
                          {analysisResult.comparison === 'above' ? 'Above' : 'Below'} industry average
                        </small>
                        <ProgressBar
                          now={analysisResult.overallScore}
                          className="mt-1"
                          variant={analysisResult.overallScore >= 70 ? "success" : "warning"}
                        />
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={4}>
                  <Card className="text-center h-100 shadow-sm">
                    <Card.Body>
                      <h6 className="text-muted">Industry Benchmark</h6>
                      <div className="d-flex justify-content-around my-3">
                        <div>
                          <h5>{analysisResult.benchmarkComparison.industryAverage}</h5>
                          <small>Industry Avg</small>
                        </div>
                        <div>
                          <h5>{analysisResult.benchmarkComparison.topPerformer}</h5>
                          <small>Top Performer</small>
                        </div>
                      </div>
                      <Badge bg="info">
                        {analysisResult.benchmarkComparison.percentile}% of Top Performer
                      </Badge>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={4}>
                  <Card className="h-100 shadow-sm">
                    <Card.Body className="text-center">
                      <h6 className="text-muted">Recommendations</h6>
                      {analysisResult.weaknesses.length > 0 ? (
                        <>
                          <p className="small">Focus on improving:</p>
                          <ListGroup variant="flush">
                            {analysisResult.weaknesses.map((factor, idx) => (
                              <ListGroup.Item key={idx} className="px-0 py-1 small">
                                {factor.factorName}
                              </ListGroup.Item>
                            ))}
                          </ListGroup>
                        </>
                      ) : (
                        <p className="text-success">
                          <i className="fas fa-check-circle me-2"></i>
                          All factors are performing well
                        </p>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Row className="mb-4">
                <Col md={6}>
                  <Card className="h-100 shadow-sm">
                    <Card.Header>
                      <h6 className="mb-0">Factor Performance</h6>
                    </Card.Header>
                    <Card.Body>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                          data={analysisResult.factorBreakdown}
                          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="factorName" tick={{ fontSize: 10 }} />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="score" name="Score">
                            {analysisResult.factorBreakdown.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={getScoreColor(entry.score)} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={6}>
                  <Card className="h-100 shadow-sm">
                    <Card.Header>
                      <h6 className="mb-0">Performance Spectrum</h6>
                    </Card.Header>
                    <Card.Body>
                      <ResponsiveContainer width="100%" height={300}>
                        <RadarChart outerRadius="80%" data={analysisResult.factorBreakdown}>
                          <PolarGrid />
                          <PolarAngleAxis dataKey="factorName" tick={{ fontSize: 10 }} />
                          <PolarRadiusAxis angle={30} domain={[0, 100]} />
                          <Radar
                            name="Score"
                            dataKey="score"
                            stroke="#8884d8"
                            fill="#8884d8"
                            fillOpacity={0.6}
                          />
                          <Tooltip />
                        </RadarChart>
                      </ResponsiveContainer>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Row>
                <Col>
                  <Card className="shadow-sm">
                    <Card.Header>
                      <h6 className="mb-0">Detailed Factor Analysis</h6>
                    </Card.Header>
                    <Card.Body>
                      <Tabs defaultActiveKey="strengths" className="mb-3">
                        <Tab eventKey="strengths" title="Strengths">
                          <ListGroup variant="flush">
                            {analysisResult.strengths.map((factor, idx) => (
                              <ListGroup.Item key={idx}>
                                <div className="d-flex justify-content-between align-items-center">
                                  <div>
                                    <h6 className="mb-1">{factor.factorName}</h6>
                                    <small className="text-muted">
                                      Score: {factor.score.toFixed(1)} - {getScoreLabel(factor.score)}
                                    </small>
                                  </div>
                                  <Badge bg="success">Strength</Badge>
                                </div>
                                <ProgressBar
                                  now={factor.score}
                                  variant="success"
                                  className="mt-2"
                                />
                              </ListGroup.Item>
                            ))}
                          </ListGroup>
                        </Tab>
                        <Tab eventKey="weaknesses" title="Improvement Areas">
                          <ListGroup variant="flush">
                            {analysisResult.weaknesses.map((factor, idx) => (
                              <ListGroup.Item key={idx}>
                                <div className="d-flex justify-content-between align-items-center">
                                  <div>
                                    <h6 className="mb-1">{factor.factorName}</h6>
                                    <small className="text-muted">
                                      Score: {factor.score.toFixed(1)} - {getScoreLabel(factor.score)}
                                    </small>
                                  </div>
                                  <Badge bg="warning">Needs Improvement</Badge>
                                </div>
                                <ProgressBar
                                  now={factor.score}
                                  variant="warning"
                                  className="mt-2"
                                />
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  className="mt-2"
                                  onClick={() => handleSubFactorClick(factor)}
                                >
                                  View Details
                                </Button>
                              </ListGroup.Item>
                            ))}
                          </ListGroup>
                        </Tab>
                        <Tab eventKey="all" title="All Factors">
                          <ListGroup variant="flush">
                            {analysisResult.factorBreakdown.map((factor, idx) => (
                              <ListGroup.Item key={idx}>
                                <div className="d-flex justify-content-between align-items-center">
                                  <div>
                                    <h6 className="mb-1">{factor.factorName}</h6>
                                    <small className="text-muted">
                                      Score: {factor.score.toFixed(1)} - {getScoreLabel(factor.score)}
                                    </small>
                                  </div>
                                  <Badge bg={factor.score >= 70 ? "success" : "warning"}>
                                    {factor.score >= 70 ? "Strength" : "Needs Improvement"}
                                  </Badge>
                                </div>
                                <ProgressBar
                                  now={factor.score}
                                  variant={factor.score >= 70 ? "success" : "warning"}
                                  className="mt-2"
                                />
                              </ListGroup.Item>
                            ))}
                          </ListGroup>
                        </Tab>
                      </Tabs>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </>
          ) : (
            <Alert variant="info">
              <Alert.Heading>No Data Available</Alert.Heading>
              <p>Please select factors to analyze or check your data connection.</p>
            </Alert>
          )}
        </Col>
      </Row>

      {/* SubFactor Detail Modal */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedSubFactor?.factorName} Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedSubFactor && (
            <>
              <h6>Sub-Factor Performance</h6>
              <ListGroup variant="flush">
                {selectedSubFactor.subFactors.map((subFactor, idx) => (
                  <ListGroup.Item key={idx}>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <strong>{subFactor.name}</strong>
                        <br />
                        <small className="text-muted">{subFactor.description}</small>
                      </div>
                      <Badge bg={subFactor.score >= 70 ? "success" : "warning"}>
                        {subFactor.score}
                      </Badge>
                    </div>
                    <ProgressBar
                      now={subFactor.score}
                      variant={subFactor.score >= 70 ? "success" : "warning"}
                      className="mt-2"
                    />
                    <div className="mt-2">
                      <small>
                        <strong>Scale:</strong> {subFactor.scale?.join(' → ')}
                      </small>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default GCCImpactAnalyzer;